/*
 * Reemplazo propio de ngx-materialize (abandonada en 2018).
 *
 * La apuesta de este spike: replicar el MISMO selector y los MISMOS @Input que exponia
 * ngx-materialize, de modo que las 522 etiquetas <mz-*> de las 27 plantillas del
 * proyecto no necesiten ninguna edicion. El CSS y el JS de Materialize son los que el
 * proyecto ya trae vendorizados en src/assets/plugins, asi que el aspecto no cambia.
 *
 * Markup que espera Materialize 0.100 y que estos componentes reproducen:
 *
 *   <div class="input-field">
 *     <input id="x" type="text" class="validate">
 *     <label for="x">ETIQUETA</label>
 *   </div>
 */
import {
	AfterViewInit,
	Component,
	Directive,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
	contentChild,
	inject,
} from '@angular/core';

/** Contrato que el contenedor necesita del control que proyecta. */
export interface MzControl {
	/** Texto de la etiqueta flotante; acepta entidades HTML como &Oacute;. */
	label?: string;
	/** id del control, para el atributo for de la etiqueta. */
	controlId: string;
}

/** Base de los contenedores: toma la etiqueta y el id del control proyectado. */
@Directive()
abstract class MzContainerBase {
	abstract control: MzControl | undefined;

	get label(): string {
		return this.control?.label ?? '';
	}

	get controlId(): string {
		return this.control?.controlId ?? '';
	}
}

/** Base de los controles: resuelve el id a partir del atributo id del elemento. */
@Directive()
abstract class MzControlBase implements MzControl, OnInit {
	@Input() label?: string;

	protected readonly host = inject(ElementRef<HTMLElement>);

	controlId = '';

	/** Clases que Materialize espera en el control. */
	protected abstract clases(): string[];

	ngOnInit(): void {
		const el = this.host.nativeElement as HTMLElement;
		this.controlId = el.getAttribute('id') ?? '';
		for (const c of this.clases()) {
			el.classList.add(c);
		}
	}
}

// ---------------------------------------------------------------- input / text

@Directive({ selector: 'input[mz-input], textarea[mz-textarea]' })
export class MzInputDirective extends MzControlBase {
	protected clases(): string[] {
		const el = this.host.nativeElement as HTMLElement;
		return el.tagName === 'TEXTAREA' ? ['materialize-textarea'] : [];
	}
}

@Component({
	selector: 'mz-input-container, mz-textarea-container',
	// Sin encapsulacion: el CSS de Materialize es global y depende de la jerarquia.
	encapsulation: ViewEncapsulation.None,
	imports: [],
	template: `
		<div class="input-field">
			<ng-content />
			@if (label) {
				<label [attr.for]="controlId" [innerHTML]="label"></label>
			}
		</div>
	`,
})
export class MzInputContainerComponent extends MzContainerBase {
	// Un contenedor anidado dentro de otro -patron que usa el proyecto- no encuentra
	// control propio; en ese caso no pinta etiqueta y solo aporta la rejilla.
	readonly controlRef = contentChild(MzInputDirective);

	get control(): MzControl | undefined {
		return this.controlRef();
	}
}

// ---------------------------------------------------------------------- select

@Directive({ selector: 'select[mz-select]' })
export class MzSelectDirective extends MzControlBase implements AfterViewInit, OnDestroy {
	/** Primera opcion deshabilitada que Materialize usa como texto guia. */
	@Input() placeholder?: string;

	protected clases(): string[] {
		return [];
	}

	/*
	 * La inicializacion vive AQUI, en la directiva del propio <select>, y no en
	 * mz-select-container.
	 *
	 * Materialize 0.100 oculta todos los <select> nativos con una regla global
	 * (`select { display: none }`, materialize.css:6962) y espera que material_select()
	 * los reemplace por su desplegable. Si nadie la llama, el combo no se ve y **no hay
	 * ningun error de consola**: el elemento esta en el DOM, solo invisible.
	 *
	 * Al principio esto lo hacia el ngAfterViewInit del contenedor, y eso dejaba fuera
	 * los <select mz-select> que van sueltos dentro de un <div class="input-field">, sin
	 * contenedor. En el proyecto hay 2 asi -el combo de ESQUEMA del login y uno en
	 * modaledit-config-esqu- contra 44 que si usan contenedor. Los 2 sueltos eran
	 * justamente los que no se pintaban.
	 *
	 * Poniendolo en la directiva quedan cubiertos los 46, con o sin contenedor.
	 */
	private observador?: MutationObserver;
	private reconstruyendo = false;
	private propagando = false;

	ngAfterViewInit(): void {
		const el = this.host.nativeElement as HTMLSelectElement;
		this.reconstruye(el);

		/*
		 * Y hay que RECONSTRUIRLO cuando cambien las <option>.
		 *
		 * material_select() toma una foto de las <option> que existen en ese momento y
		 * construye con ellas su propio <ul>. Casi todos los combos del proyecto se
		 * llenan con *ngFor sobre un arreglo que llega por HTTP, es decir DESPUES de
		 * ngAfterViewInit: el desplegable queda construido sobre la lista vacia y no se
		 * vuelve a tocar. El sintoma es un combo que aparece pero no muestra nada, sin
		 * error de consola, aunque el backend haya respondido bien.
		 *
		 * ngx-materialize hacia esta reconstruccion por su cuenta; mi primera version del
		 * envoltorio no, y por eso el combo de ESQUEMA del login salia vacio.
		 *
		 * Se usa MutationObserver y no un hook de ciclo de vida porque el momento en que
		 * *ngFor inserta las opciones no esta ligado a un hook concreto de esta directiva.
		 * Observar solo `childList` del propio <select> es seguro: material_select
		 * manipula elementos hermanos y el padre (envuelve y desenvuelve el select), no
		 * los hijos <option>.
		 */
		this.observador = new MutationObserver(() => this.reconstruye(el));
		this.observador.observe(el, { childList: true });
	}

	ngOnDestroy(): void {
		this.observador?.disconnect();
	}

	/** Guarda de reentrada, por si alguna version de Materialize tocara los hijos. */
	private reconstruye(el: HTMLSelectElement): void {
		if (this.reconstruyendo) {
			return;
		}
		this.reconstruyendo = true;
		try {
			inicializaSelect(el);
			this.puenteaCambio(el);
		} finally {
			this.reconstruyendo = false;
		}
	}

	/*
	 * Puente entre el evento de jQuery y el DOM nativo.
	 *
	 * Al elegir una opcion del desplegable, materialize.js hace:
	 *
	 *     $select.find('option').eq(i).prop('selected', selected);
	 *     $select.trigger('change');
	 *
	 * `.trigger()` de jQuery ejecuta los manejadores registrados POR jQUERY, pero no
	 * emite un evento DOM. Angular escucha con addEventListener -tanto el
	 * SelectControlValueAccessor de formControlName como el (change) de la plantilla-,
	 * asi que nunca se enteraba: el valor quedaba puesto en el <select> y el control del
	 * formulario seguia vacio.
	 *
	 * En el login el sintoma era que el boton Aceptar no se desbloqueaba, porque
	 * `loading` solo pasa a false dentro de continuar(), que cuelga de ese (change).
	 *
	 * Asi que se escucha el evento de jQuery y se reemite como evento nativo. El
	 * manejador se registra con nombre propio (`change.mzPuente`) para poder quitarlo en
	 * cada reconstruccion sin tocar los de Materialize, y la guarda de reentrada evita
	 * el bucle: jQuery escucha por addEventListener, asi que tambien oiria el evento
	 * nativo que acabamos de emitir.
	 */
	private puenteaCambio(el: HTMLSelectElement): void {
		if (typeof $ !== 'function') {
			return;
		}
		const jq = $(el);
		jq.off?.('change.mzPuente');
		if (typeof jq.on !== 'function') {
			return;
		}
		jq.on('change.mzPuente', () => {
			if (this.propagando) {
				return;
			}
			this.propagando = true;
			try {
				el.dispatchEvent(new Event('change', { bubbles: true }));
			} finally {
				this.propagando = false;
			}
		});
	}
}

@Component({
	selector: 'mz-select-container',
	encapsulation: ViewEncapsulation.None,
	template: `
		<div class="input-field">
			<ng-content />
			@if (label) {
				<label [attr.for]="controlId" [innerHTML]="label"></label>
			}
		</div>
	`,
})
export class MzSelectContainerComponent extends MzContainerBase {
	readonly controlRef = contentChild(MzSelectDirective);

	get control(): MzControl | undefined {
		return this.controlRef();
	}

	/*
	 * Ya no inicializa el select: lo hace MzSelectDirective, que esta en el propio
	 * <select> y por tanto cubre tambien los que van sin contenedor. Dejarlo aqui
	 * tambien provocaba una doble inicializacion en los 44 que si tienen contenedor.
	 */
}

declare const $:
	| ((objetivo: string | Element) => {
			material_select?: () => void;
			on?: (evento: string, manejador: () => void) => void;
			off?: (evento: string) => void;
	  })
	| undefined;

/**
 * Pide a Materialize que construya su desplegable sobre un `<select>` concreto.
 *
 * Recibe el elemento, no un selector: la version anterior hacia `$('select')`, es decir
 * reinicializaba TODOS los select de la pagina cada vez que se pintaba cualquier
 * contenedor. Con el datatable, que recicla filas, eso es trabajo repetido y puede
 * perder el estado de los que ya estaban montados.
 */
function inicializaSelect(el: Element): void {
	if (typeof $ !== 'function') {
		return;
	}
	const jq = $(el);
	if (typeof jq.material_select === 'function') {
		jq.material_select();
	}
}

// -------------------------------------------------------------------- checkbox

@Directive({ selector: 'input[mz-checkbox]' })
export class MzCheckboxDirective extends MzControlBase {
	/** Variante visual de Materialize. */
	@Input() filledIn?: boolean;

	protected clases(): string[] {
		return this.filledIn ? ['filled-in'] : [];
	}
}

@Component({
	selector: 'mz-checkbox-container',
	encapsulation: ViewEncapsulation.None,
	template: `
		<ng-content />
		@if (label) {
			<label [attr.for]="controlId" [innerHTML]="label"></label>
		}
	`,
})
export class MzCheckboxContainerComponent extends MzContainerBase {
	readonly controlRef = contentChild(MzCheckboxDirective);

	get control(): MzControl | undefined {
		return this.controlRef();
	}
}

// ---------------------------------------------------------------- radio button

@Directive({ selector: 'input[mz-radio-button]' })
export class MzRadioButtonDirective extends MzControlBase {
	/** Variante con hueco de Materialize. */
	@Input() withGap?: boolean;

	protected clases(): string[] {
		return this.withGap ? ['with-gap'] : [];
	}
}

@Component({
	selector: 'mz-radio-button-container',
	encapsulation: ViewEncapsulation.None,
	template: `
		<ng-content />
		@if (label) {
			<label [attr.for]="controlId" [innerHTML]="label"></label>
		}
	`,
})
export class MzRadioButtonContainerComponent extends MzContainerBase {
	readonly controlRef = contentChild(MzRadioButtonDirective);

	get control(): MzControl | undefined {
		return this.controlRef();
	}
}


// ----------------------------------------------------------------- boton

/**
 * 153 botones del proyecto usan mz-button, 98 de ellos con [flat] y 6 con [large].
 * Materialize solo pide las clases btn / btn-flat / btn-large.
 */
@Directive({ selector: '[mz-button]' })
export class MzButtonDirective implements OnInit {
	@Input() flat?: boolean | string;
	@Input() large?: boolean | string;
	/*
	 * El nombre que usaba ngx-materialize es `float`, no `floating`: asi lo ligan los 2
	 * botones de modal-diff. Se aceptan los dos para no tocar plantillas.
	 *
	 * Este hueco no lo detecta la auditoria de selectores, que solo compara nombres de
	 * selector: el selector [mz-button] existia y estaba cubierto, lo que faltaba era un
	 * @Input de dentro. Lo atrapo el compilador, porque una propiedad LIGADA desconocida
	 * si es error. Es la diferencia con mz-modal-close, que al ser atributo plano paso
	 * tres spikes en silencio.
	 */
	@Input() float?: boolean | string;
	@Input() floating?: boolean | string;

	private readonly host = inject(ElementRef<HTMLElement>);

	ngOnInit(): void {
		const el = this.host.nativeElement as HTMLElement;
		el.classList.add(this.esVerdadero(this.flat) ? 'btn-flat' : 'btn');
		if (this.esVerdadero(this.large)) {
			el.classList.add('btn-large');
		}
		if (this.esVerdadero(this.float) || this.esVerdadero(this.floating)) {
			el.classList.add('btn-floating');
		}
	}

	private esVerdadero(v: boolean | string | undefined): boolean {
		return v === true || v === 'true' || v === '';
	}
}

/** Lo que una plantilla portada necesita importar, en un solo arreglo. */
export const MZ_FORM = [
	MzInputContainerComponent,
	MzInputDirective,
	MzSelectContainerComponent,
	MzSelectDirective,
	MzCheckboxContainerComponent,
	MzCheckboxDirective,
	MzRadioButtonContainerComponent,
	MzRadioButtonDirective,
	MzButtonDirective,
] as const;
